import { createContext, useContext, type ReactNode } from "react";
import { cn } from "../lib/cn";

export type KpiSize = "default" | "compact";

const KpiSizeContext = createContext<KpiSize>("default");

export type KpiProps = {
  label: string;
  value: string | number;
  unit?: string;
  delta?: string;
  deltaTone?: "neutral" | "success" | "warning" | "danger";
  /** Comes from the enclosing `KpiRow`; set it here only on a stat standing alone. */
  size?: KpiSize;
  className?: string;
};

const deltaTones = {
  neutral: "text-ink-3",
  success: "text-success",
  warning: "text-warning",
  danger: "text-danger",
} as const;

const valueSizes: Record<KpiSize, string> = {
  default: "text-2xl md:text-3xl",
  compact: "text-xl",
};

/**
 * A bare stat: label, value, an optional line under it. Inside a `KpiRow` the label and the value are subgrid tracks,
 * so the values of neighbouring stats start on one line even when a label wraps; the delta stays under its own value.
 * A value wider than its column breaks rather than running into the next stat, and the unit moves down whole — so keep
 * currencies in `unit`, not in the value.
 *
 * A stat earns its place only on a page that monitors or reports (the skill's counts ladder, rung 3); a list page's
 * figures go in the `PageHeader` description instead.
 */
export function Kpi({ label, value, unit, delta, deltaTone = "neutral", size, className }: KpiProps) {
  const rowSize = useContext(KpiSizeContext);
  const effective = size ?? rowSize;
  return (
    <div className={cn("grid min-w-0 grid-rows-subgrid row-span-2 gap-y-1", className)}>
      <span className="text-2xs font-medium uppercase tracking-wider text-ink-3">{label}</span>
      <div className="flex min-w-0 flex-col gap-1">
        <span className={cn("font-medium tabular-nums tracking-tight text-ink [overflow-wrap:anywhere]", valueSizes[effective])}>
          {value}
          {unit && (
            <>
              {" "}
              <span className="whitespace-nowrap font-sans text-sm text-ink-3">{unit}</span>
            </>
          )}
        </span>
        {delta && <span className={cn("text-xs", deltaTones[deltaTone])}>{delta}</span>}
      </div>
    </div>
  );
}

export type KpiRowProps = {
  /** Stats per row from `md`; two per row below it. */
  cols?: 3 | 4 | 5;
  /**
   * `default` when the figures are the section's content (Reports, Measures, a record's summary);
   * `compact` when the row sits above a table and the table is the point — values one step smaller, tighter rule.
   */
  size?: KpiSize;
  className?: string;
  children: ReactNode;
};

const colClasses = { 3: "md:grid-cols-3", 4: "md:grid-cols-4", 5: "md:grid-cols-5" } as const;

const rowSizes: Record<KpiSize, string> = {
  default: "gap-y-6 pt-4",
  compact: "gap-y-4 pt-3",
};

/** Several stats under one shared rule: no boxes, no per-tile rules, labels and values aligned across the row. */
export function KpiRow({ cols = 4, size = "default", className, children }: KpiRowProps) {
  return (
    <KpiSizeContext.Provider value={size}>
      <div data-slot="kpi-row" className={cn("grid grid-cols-2 gap-x-6 border-t border-line-strong md:gap-x-8", colClasses[cols], rowSizes[size], className)}>
        {children}
      </div>
    </KpiSizeContext.Provider>
  );
}
